<template>
  <v-row>
    <!-- Reviews list -->
    <v-col cols="12" lg="4" md="6" class="tw-border tw-rounded tw-mt-3">
      <v-container>
        <div class="sr-title tw-mt-4 tw-mb-4">
          {{ $t("reviews.list.filters.search_title") }}
        </div>
        <v-text-field v-model="filter.search" class="mt-4" dense flat full-width single-line>
          <template #prepend-inner>
            <v-icon left small>fa-search</v-icon>
          </template>
          <template v-if="filter.search.length > 0" #append>
            <v-icon right small @click="filter.search = ''">fa-times</v-icon>
          </template>
        </v-text-field>
      </v-container>

      <v-list subheader two-line>
        <!-- Scrollable list of reviews -->
        <v-list-item-group style="height: calc(100vh - 250px);overflow-y: auto;">
          <div
            v-for="review in filteredReviews"
            :id="review.id + review.database"
            :key="review.id + review.database"
            @click="selectReview(review)"
          >
            <v-list-item v-if="check(review)" class="tw-border" color="primary">
              <v-list-item-content class="tw-my-1">
                <!-- Information about the current item -->
                <v-list-item-title class="mt-2 tw-flex tw-items-center tag-margin">
                  <span>
                    <v-icon x-small>fa-globe</v-icon>
                    {{ review.domain.host }}
                  </span>
                  <v-chip x-small>
                    <v-icon color="secondary" left x-small>fa-star</v-icon>
                    {{ review.rating }} / 5
                  </v-chip>
                  <v-chip v-if="Boolean(review.private)" :title="$t('reviews.list.private')" color="warning" x-small>
                    <v-icon x-small>fa-sticky-note</v-icon>
                  </v-chip>
                  <v-chip x-small>
                    <v-icon left x-small>fa-history</v-icon>
                    {{ review.updated_at }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <div v-if="review.group_id > 0">
                    <span v-html="$t('reviews.list.group.intro.me')"></span>
                    <b>{{ getGroupName(review.group_id) }}</b>
                    <br />
                  </div>
                  <div v-if="review.product">
                    <span v-html="$t('reviews.list.product.intro.me')"></span>
                    <b>{{ review.product.commercial_name }}</b>
                  </div>
                  <div v-if="review.thematic">
                    <span v-html="$t('reviews.list.thematic.intro.me')"></span>
                    <b>{{ review.thematic.thematic }}</b>
                  </div>
                  <span v-if="review.message.length < 64">{{ review.message }}</span>
                  <span v-else>{{ review.message.slice(0, 64) }}...</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list-item-group>
      </v-list>
    </v-col>

    <!-- Selected review -->
    <v-col v-if="hasReview" cols="12" lg="8" md="6">
      <div class="mx-auto tw-border tw-rounded">
        <v-container>
          <span class="sr-title">
            {{ $t("reviews.list.item") }}
          </span>
          <v-card-text v-if="review !== null" class="px-6">
            <v-row>
              <v-col class="tw-flex tw-items-center tw-justify-between">
                <span>
                  <v-avatar size="48" v-if="review.user.avatar">
                    <img :alt="review.user.user_name" :src="review.user.avatar" />
                  </v-avatar>

                  <span
                    v-if="getName(review).length > 0"
                    :title="getUserName(review)"
                    class="blue--text"
                    v-html="review.solicitation_id > 0 ? getName(review) : getName(review)"
                  ></span>
                  <span class="blue--text" v-else>@{{ getUserName(review) }}</span>
                </span>
                <span>
                  <span class="ml-2">
                    <b> {{ getOrigin(review) }} ©</b>
                  </span>
                  <span class="ml-2">{{ review_date }}</span>
                  <span class="ml-2">
                    <v-icon color="secondary" style="margin-bottom: 3px;" x-small>fa-star</v-icon>
                    {{ review.rating }}/5
                  </span>
                  <span v-if="review.like && review.like.counter > 0" class="ml-2" x-small>
                    <v-icon x-small class="pb-1">fa-thumbs-up</v-icon>
                    {{ review.like.counter }}
                  </span>
                </span>
              </v-col>
            </v-row>

            <div v-if="review.product || review.thematic" class="mt-4">
              <span v-if="review.product" class="mt-2">
                <span v-html="$t('reviews.list.product.intro.me')"></span>
                <b>{{ review.product.commercial_name }}</b>
              </span>
              <span v-if="review.thematic" class="mt-2">
                <span v-html="$t('reviews.list.thematic.intro.me')"></span>
                <b>{{ review.thematic.thematic }}</b>
              </span>
            </div>

            <!-- Here is the form to change/update to the current review -->
            <div v-if="editMod">
              <span v-if="review.group_id > 0">
                <span v-html="$t('reviews.list.group.intro.me')"></span>
                <b>{{ getGroupName(review.group_id) }}</b>
                <br />
              </span>
              <v-select v-model="review.group_id"
                :items="groups"
                :label="$t('reviews.list.update.group_public')"
                class="mt-3"
                clearable
                col="6"
                item-text="group"
                item-value="id"
              ></v-select>
              <v-textarea v-model="review.message"
                :label="$t('reviews.list.update.message')"
                auto-grow
                class="mt-6"
                name="message"
                no-resize
              />
              <div class="mb-4">
                <v-text-field v-model="review.order_date"
                    type="date"
                    :label="$t('dashboard.reviewsModeration.response.context.order_date')"
                    hide-details="auto"
                ></v-text-field>
                <v-text-field v-model="review.order_number"
                  :label="$t('dashboard.reviewsModeration.response.context.order_number')"
                  hide-details="auto"
              ></v-text-field>
              </div>
              <v-btn color="primary" small @click="update">
                {{ $t("reviews.list.update.buttons.update") }}
              </v-btn>
              <v-btn class="ml-4" small @click="removeDialog = true">
                {{ $t("reviews.list.update.buttons.delete") }}
              </v-btn>
            </div>
            <div v-else class="mt-4">
              <p v-if="review.group_id > 0">
                <span v-html="$t('reviews.list.group.intro.me')"></span>
                <b>{{ getGroupName(review.group_id) }}</b>
              </p>
              <u>{{ $t('dashboard.reviewsModeration.review.message') }}</u>
              <p style="padding:5px 10px; background-color: #e7e9eb;" v-text="review.message"/>
              <div class="mt-4">
                <div style="width:45%;float:left;" >
                  <u>{{ $t('dashboard.reviewsModeration.review.order_date') }}</u>
                  <p style="padding:5px 10px; background-color: #e7e9eb;" v-text="getOrderDate"/>
                </div>
                <div style="width:45%;float:left;" >
                  <u>{{ $t('dashboard.reviewsModeration.review.order_number') }}</u>
                  <p style="padding:5px 10px; background-color: #e7e9eb;" v-text="getOrderNumber"/>
                </div>
              </div>
              <div>
                <!-- Link to the review -->
                <v-btn x-small @click="toggleEditMod">
                  {{ $t("reviews.list.update.edit") }}
                </v-btn>
                <v-btn class="ml-2" :title="domain.host" x-small @click="openExternalLink">
                  {{ $t("reviews.list.review-link") }}
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-container>
      </div>

      <v-row v-if="hasResponse && response.message !== ''" align="center" class=" mt-4">
        <v-col cols="12">
          <div class="mx-auto tw-border tw-rounded">
            <v-container>
              <span class="sr-title">
                {{ $t("reviews.list.response") }}
              </span>
              <v-card-text class="tw-pt-6">
                <p style="white-space: pre-wrap;" v-text="response.message" />
              </v-card-text>
            </v-container>
          </div>
        </v-col>
      </v-row>
    </v-col>

    <!-- Dialog for confirmations -->
    <v-dialog v-model="removeDialog" max-width="390">
      <v-card>
        <v-card-title class="headline">
          {{ $t("reviews.list.dialogs.remove.title") }}
        </v-card-title>

        <v-card-text>
          {{ $t("reviews.list.dialogs.remove.text") }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn color="" text @click="removeDialog = false">
            {{ $t("common.no") }}
          </v-btn>

          <!-- Yes button will call the remove method to delete the selected review from the list -->
          <v-btn color="red darken-1" text @click="remove">
            {{ $t("common.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel dialog of the edit mod -->
    <v-dialog v-model="cancelDialog" max-width="390">
      <v-card>
        <v-card-title class="headline">
          {{ $t("reviews.list.dialogs.cancel.title") }}
        </v-card-title>

        <v-card-text>
          {{ $t("reviews.list.dialogs.cancel.text") }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn text @click="cancelDialog = false">
            {{ $t("common.no") }}
          </v-btn>

          <!-- Yes button will call the validate method to change the moderation status -->
          <v-btn color="red darken-1" text @click="cancel">
            {{ $t("common.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { Types } from "@/store/modules/publications"
import { Types as TypesDialog } from "../../../../store/modules/dialog"
import { isset } from "@/helpers"
import { Types as TypesLoading } from "@/store/modules/loading"

export default {
  name: "List",
  data: () => ({
    loadingReviews: false, // true during reviews load from server
    selected: null, // Index of the selected review in the list
    reviews: [], // Reviews,
    filter: {
      search: "",
      group: 0,
    },
    groups: [],
    review: null, // Selected review
    domain: null, // Domain / Host related to the selected review
    url: null, // Url related to the selected review
    user: null, // User related to the selected review
    editMod: false, // Toggle edition state of the selected review
    locked: false, //Deadlock state
    removeDialog: false, // Dialog state for the remove action
    cancelDialog: false, // Dialog to valid the cancellation
    snapshot: {}, // Snapshot of the selected review before is modification
  }),
  mounted() {
    this.loadingReviews = true
    this.$store.commit(TypesLoading.mutations.LOADING_YES)
    //-- no more Groups this.fetchGroups()
    this.fetchReviews()
  },
  beforeDestroy() {
    if (this.locked) {
      this.free().then(() => {
        this.locked = false
      })
    }
  },
  computed: {
    isOrder() {
      return this.review.from_order===1
    },
    hasOrderDate() {
      if(this.review.from_order===1) {
        if(this.review.details_json) {
          let details = JSON.parse(this.review.details_json)
          return details.hasOwnProperty('order_date')
        }
      }
      return false
    },
    getOrderDate() {
      if(this.review.details_json) {
        let details = JSON.parse(this.review.details_json)
        if(details.hasOwnProperty('order_date')) {
          return details.order_date
        }
      }
      return '-'
    },
    hasOrderNumber() {
      if(this.review.from_order===1) {
        if(this.review.details_json) {
          let details = JSON.parse(this.review.details_json)
          return details.hasOwnProperty('order_number')
        }
      }
      return false
    },
    getOrderNumber() {
      if(this.review.details_json) {
        let details = JSON.parse(this.review.details_json)
        if(details.hasOwnProperty('order_number')) {
          return details.order_number
        }
      }
      return '-'
    },
    review_date() {
      let dt = new Date(this.review.created_at)
      return dt.toLocaleDateString("fr-fr")
    },
    // Check the moderation status
    moderated() {
      return this.review ? this.review.moderate : false
    },
    hasReview() {
      return isset(this.review)
    },
    hasResponse() {
      return isset(this.review.response)
    },
    filteredReviews() {
      if (this.filter.search.length <= 0) {
        return this.reviews
      }

      return this.reviews.filter(review => {
        return (
          review.message.toLowerCase().includes(this.filter.search.toLowerCase()) ||
          (review.response && review.response.message.toLowerCase().includes(this.filter.search.toLowerCase())) ||
          (review.url && review.url.full_path.toLowerCase().includes(this.filter.search.toLowerCase()))
        )
      })
    },
  },
  methods: {
    openExternalLink() {
      window.open(this.review.url.full_path, "_blank")
    },
    getUserName(publication) {
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        return ""
      } else {
        return `${user_name}`
      }
    },
    getName(publication) {
      const { first_name } = publication.user
      const { last_name } = publication.user
      let name = ""
      if (first_name) name = name + " " + first_name
      if (last_name) name = name + " " + last_name
      return name.trim()
    },
    displayOrigin(publication) {
      return this.$t("origin.source") + this.getOrigin(publication)
    },
    getOrigin(publication) {
      const securating = "Securating"
      const { user_name } = publication.user
      const semicolonPos = user_name.indexOf(":")
      if (semicolonPos > 0) {
        const prefix = user_name.substr(0, semicolonPos)

        switch (prefix) {
          case "anonymous":
            return securating
          default:
            return `${this.$t("origin." + prefix)}`
        }
      } else {
        return securating
      }
    },
    fetchGroups() {
      this.$axios.get("/office/me/groups").then(response => {
        this.groups = response.data
      })
    },
    fetchReviews() {
      this.$store.commit(TypesLoading.mutations.LOADING_YES)
      if (this.filter.group > 0) {
        this.$axios
          .get("/office/me/reviews/" + this.filter.group)
          .then(response => {
            this.reviews = response.data
            this.loadingReviews = false
          })
          .finally(() => this.$store.commit(TypesLoading.mutations.LOADING_NO))
      } else {
        this.$axios
          .get("/office/me/reviews/")
          .then(response => {
            this.reviews = response.data
            this.loadingReviews = false
          })
          .finally(() => this.$store.commit(TypesLoading.mutations.LOADING_NO))
      }
    },
    getGroupName(group_id) {
      let index = this.groups.findIndex(item => item.id === group_id)
      if (index > 0) {
        return this.groups[index].group
      }
      return ""
    },
    selectReview(review) {
      if (this.review && this.review.id + this.review.database === review.id + review.database) {
        this.selected = null
        this.review = null
        this.response = { message: "" }
        this.domain = null
        this.url = null
        this.user = null
        return
      }

      if (this.locked) {
        this.free().then(() => {
          this.locked = false
        })
      }

      this.$axios
        .post("office/common/deadlocks/check", {
          publication_id: review.id,
          database: review.database,
        })
        .then(() => {
          if (review.hasOwnProperty("response")) {
            this.response = review.response
          }
          if (review.hasOwnProperty("domain")) {
            this.domain = review.domain
          }
          if (review.hasOwnProperty("url")) {
            this.url = review.url
          }
          if (review.hasOwnProperty("user")) {
            this.user = review.user
          }
          if (this.response === null) {
            this.response = { message: "" }
          }

          this.review = review

          // take a snapshot of the selected review
          this.snapshot = JSON.parse(
            JSON.stringify({
              review: this.review,
              response: this.response,
              domain: this.domain,
              url: this.url,
              user: this.user,
            })
          )

          this.editMod = false
        })
        .catch(error => {
          this.$emit("deadlock", error.response.data)
        })
    },
    // Check if the review object contain all the necessary information's to be in the list
    check(review) {
      return (
        review.hasOwnProperty("domain") &&
        review.domain &&
        review.hasOwnProperty("url") &&
        review.url &&
        review.hasOwnProperty("user") &&
        review.user
      )
    },
    // Update the selected review
    update() {
      if (this.review.group_id === undefined) this.review.group_id = 0
      this.$axios.patch("office/me/review", { review: this.review }).then(response => {
        const index = this.reviews.findIndex(r => r.id === this.review.id && r.database === this.review.database)

        //-- Update the local list
        //this.reviews[index] = this.review
        this.reviews[index] = response.data.data

        // Dispatch changes
        //this.$store.dispatch(Types.actions.REVIEWS, reviews)

        this.free().then(() => {
          this.editMod = false
          this.locked = false
        })
      })
    },
    // Remove the selected review
    remove() {
      // Make the http request to the api
      this.$axios
        .delete("office/me/review", {
          data: { review: this.review },
        })
        .then(() => {
          // Get reviews without the deleted entry
          //const reviews = this.$store.state.publications.reviews.filter(
          //  r => !(r.id === this.review.id && r.database === this.review.database)
          //)

          // Dispatch changes
          //this.$store.dispatch(Types.actions.REVIEWS, reviews)
          //-- Update the local list
          this.reviews = this.reviews.filter(r => !(r.id === this.review.id && r.database === this.review.database))
          // Reset the selected review
          this.selected = null
          this.review = null
          this.response = { message: "" }
          this.domain = null
          this.url = null
          this.user = null
          this.locked = false
        })

      // Hide the confirmation dialog
      this.removeDialog = false
      this.editMod = false
    },
    // Toggle edit mod
    toggleEditMod() {
      if (this.editMod === false) {
        this.set().then(() => {
          this.editMod = true
          this.locked = true
        })
      } else {
        this.$store.dispatch(TypesDialog.actions.CONFIRM, {
          message: this.$t("reviews.list.dialogs.cancel.text"),
          callback: () => {
            this.free().then(() => {
              this.editMod = false
              this.locked = false
            })
          },
        })
      }
    },
    // Cancel modification (using snapshot)
    cancel() {
      if (this.snapshot.hasOwnProperty("review") && this.snapshot.hasOwnProperty("response")) {
        this.review = this.snapshot.review
        this.response = this.snapshot.response

        let reviews = this.$store.state.publications.reviews

        const index = reviews.findIndex(r => r.id === this.review.id && r.database === this.review.database)

        reviews[index] = this.review

        // Dispatch changes
        this.$store.dispatch(Types.actions.REVIEWS, reviews)
      }
      this.editMod = false
      this.cancelDialog = false
    },
    set() {
      return this.$axios.post("office/common/deadlocks/set", {
        publication_id: this.review.id,
        database: this.review.database,
      })
    },
    free() {
      return this.$axios.post("office/common/deadlocks/free", {
        publication_id: this.review.id,
        database: this.review.database,
      })
    },
  },
}
</script>
